import axios from "../helpers/axios";

/*
 * Отложенная отправка: сообщение лежит на сервере и само уходит в чат в
 * назначенное время — даже если браузер закрыт. До этого собеседник его не
 * видит. Тот же сервер, что у приложения (controller/scheduledMessages.js).
 */

/** Насколько вперёд можно отложить сообщение: на месяц (31 день). */
export const SCHEDULE_MAX_DAYS = 31;

/** Самое раннее время отправки: следующая целая минута, но не ближе чем через минуту. */
export const minScheduleDate = (now = Date.now()) => new Date(Math.ceil((now + 60 * 1000) / 60000) * 60000);

/** Самое позднее время отправки: конец дня через месяц. */
export const maxScheduleDate = (now = new Date()) => (
    new Date(now.getFullYear(), now.getMonth(), now.getDate() + SCHEDULE_MAX_DAYS, 23, 59, 0, 0)
);

/** Время, которое точно примет сервер: пока выбирали, минута могла пройти. */
export const fitScheduleDate = (value) => {
    const min = minScheduleDate();
    const max = maxScheduleDate();
    const time = value instanceof Date ? value.getTime() : new Date(value).getTime();
    if (!Number.isFinite(time) || time < min.getTime()) return min;
    if (time > max.getTime()) return max;
    return new Date(time);
};

const two = (value) => String(value).padStart(2, "0");

/** Дата для <input type="datetime-local"> (по местному времени). */
export const toLocalInputValue = (date) => (
    `${date.getFullYear()}-${two(date.getMonth() + 1)}-${two(date.getDate())}T${two(date.getHours())}:${two(date.getMinutes())}`
);

/** «Сегодня, 18:00», «Завтра, 09:00», «15 октября, 12:30». */
export const scheduleText = (value) => {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    const now = new Date();
    const startOf = (item) => new Date(item.getFullYear(), item.getMonth(), item.getDate()).getTime();
    const diff = Math.round((startOf(date) - startOf(now)) / (24 * 60 * 60 * 1000));
    const time = `${two(date.getHours())}:${two(date.getMinutes())}`;
    if (diff === 0) return `Сегодня, ${time}`;
    if (diff === 1) return `Завтра, ${time}`;
    const day = date.toLocaleDateString("ru-RU", {
        day: "numeric",
        month: "long",
        year: date.getFullYear() === now.getFullYear() ? undefined : "numeric",
    });
    return `${day}, ${time}`;
};

/** Понятный текст ошибки: что ответил сервер или что нет связи. */
export const scheduledErrorText = (error, fallback = "Не получилось. Проверьте связь и попробуйте ещё раз.") => String(
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    (error?.message && error.message !== "Network Error" ? error.message : "") ||
    fallback
);

/** Мои отложенные сообщения в чате (по времени отправки). */
export const fetchScheduledMessages = async (chatId) => {
    const { data } = await axios.get("/scheduled-messages", { params: { chatId } });
    return Array.isArray(data?.items) ? data.items : [];
};

/** Отложить сообщение. Файл грузится сразу; onProgress получает проценты. */
export const createScheduledMessage = async (chatId, draft, onProgress) => {
    const form = new FormData();
    form.append("text", draft.text || "");
    form.append("sendAt", fitScheduleDate(draft.sendAt).toISOString());
    if (draft.messageKind) form.append("messageKind", draft.messageKind);
    if (draft.mediaDurationSec != null) form.append("mediaDurationSec", String(draft.mediaDurationSec));
    if (draft.replyTo) form.append("replyTo", JSON.stringify(draft.replyTo));
    if (draft.albumId) form.append("albumId", draft.albumId);
    if (draft.file) form.append("file", draft.file, draft.file.name || "file");

    const { data } = await axios.post(`/chats/${encodeURIComponent(chatId)}/scheduled-messages`, form, {
        onUploadProgress: (event) => {
            if (!draft.file || !event?.total) return;
            onProgress?.(Math.round((event.loaded * 100) / event.total));
        },
    });
    return data?.item || null;
};

/** Изменить время отправки. */
export const updateScheduledMessage = async (id, { sendAt }) => {
    const { data } = await axios.patch(`/scheduled-messages/${encodeURIComponent(id)}`, {
        sendAt: fitScheduleDate(sendAt).toISOString(),
    });
    return data?.item || null;
};

/** Отправить сейчас, не дожидаясь назначенного времени. */
export const sendScheduledNow = async (id) => {
    await axios.post(`/scheduled-messages/${encodeURIComponent(id)}/send-now`);
    return true;
};

export const deleteScheduledMessage = async (id) => {
    await axios.delete(`/scheduled-messages/${encodeURIComponent(id)}`);
    return true;
};
